/**
 * BoqInlineAddRow Component
 * Renders an inline row at the bottom of a BOQ section table to add a new line item.
 * Allows searching catalog products and inputting quantity/price directly in the table
 * without opening a modal dialog.
 */

import { Check, Loader2, Plus, Search, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
	getCatalogProduct,
	type ProductSearchResult,
	searchCatalogProducts,
} from "@/features/catalog/services/catalog-api.service";
import { paisaToRupeeInput, rupeeInputToPaisa } from "@/lib/format";
import { debounce } from "@/lib/utils";
import {
	addBOQEntry,
	formatCurrency,
	type UoM,
} from "../../services/boq/boq-api.service";

type CatalogProductItem = ProductSearchResult["products"][number];

interface BoqInlineAddRowProps {
	boqId: string;
	sectionId: string;
	nextIndex?: number;
	currencyCode: string;
	onEntryAdded: () => void;
}

const BOQ_ITEM_KINDS = ["material", "labour", "service"] as const;

export function BoqInlineAddRow({
	boqId,
	sectionId,
	nextIndex = 1,
	currencyCode,
	onEntryAdded,
}: BoqInlineAddRowProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [products, setProducts] = useState<CatalogProductItem[]>([]);
	const [selectedProduct, setSelectedProduct] =
		useState<CatalogProductItem | null>(null);
	const [isSearching, setIsSearching] = useState(false);
	const [showProductDropdown, setShowProductDropdown] = useState(false);
	// A line typed in by hand because it is not in the catalogue.
	const [isCustomItem, setIsCustomItem] = useState(false);

	const [description, setDescription] = useState("");
	const [roomName, setRoomName] = useState("");
	const [quantity, setQuantity] = useState("1");
	const [unitPrice, setUnitPrice] = useState("");
	const [uom, setUom] = useState<UoM>("piece");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const searchInputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLTableCellElement>(null);

	// Debounced product search
	const debouncedSearch = useCallback(
		debounce(async (query: string) => {
			setIsSearching(true);
			try {
				const result = await searchCatalogProducts({
					q: query.trim() || undefined,
					perPage: 10,
					itemKind: [...BOQ_ITEM_KINDS],
				});
				setProducts(result.products || []);
			} catch (err) {
				console.error("Product search failed:", err);
			} finally {
				setIsSearching(false);
			}
		}, 300),
		[],
	);

	useEffect(() => {
		if (isOpen) {
			searchInputRef.current?.focus();
			debouncedSearch("");
		}
	}, [isOpen, debouncedSearch]);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node)
			) {
				setShowProductDropdown(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const handleSelectProduct = async (prod: CatalogProductItem) => {
		setSelectedProduct(prod);
		setSearchQuery(prod.name);
		setShowProductDropdown(false);
		if (!description) {
			setDescription(prod.description || prod.name);
		}
		if (prod.uom) {
			setUom(prod.uom as UoM);
		}

		// Try loading detail to get customer price
		try {
			const detail = await getCatalogProduct(prod.id);
			if (detail?.prices && detail.prices.length > 0) {
				const defaultPrice = detail.prices[0]?.amount;
				if (defaultPrice !== undefined && defaultPrice !== null) {
					setUnitPrice(paisaToRupeeInput(defaultPrice));
				}
			}
		} catch {
			// ignore price pre-fill failure
		}
	};

	const handleChooseCustom = () => {
		setSelectedProduct(null);
		setIsCustomItem(true);
		setShowProductDropdown(false);
	};

	const resetForm = () => {
		setIsOpen(false);
		setSearchQuery("");
		setSelectedProduct(null);
		setIsCustomItem(false);
		setDescription("");
		setRoomName("");
		setQuantity("1");
		setUnitPrice("");
		setShowProductDropdown(false);
	};

	const parsedQty = Number.parseFloat(quantity);
	const safeQty = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 0;
	// An empty or half-typed price parses to NaN, which would render as "₹NaN".
	const rawPaisa = rupeeInputToPaisa(unitPrice);
	const parsedPaisa = Number.isFinite(rawPaisa) ? rawPaisa : 0;
	const liveTotalPaisa = Math.round(safeQty * parsedPaisa);
	const canSaveCustom = isCustomItem && searchQuery.trim().length > 0;

	const handleSave = async () => {
		const customName = searchQuery.trim();
		if (!selectedProduct && !canSaveCustom) {
			toast.error("Select a catalogue product or add a custom item");
			return;
		}
		if (safeQty <= 0) {
			toast.error("Quantity must be greater than 0");
			return;
		}

		setIsSubmitting(true);
		try {
			const result = await addBOQEntry(boqId, sectionId, {
				...(selectedProduct
					? { catalogProductId: selectedProduct.id }
					: { customName, uom }),
				description: description.trim() || selectedProduct?.name || customName,
				quantity: safeQty,
				unitPrice: parsedPaisa,
				roomCustomName: roomName.trim() || undefined,
			});

			if (result.success) {
				toast.success("Item added to section");
				resetForm();
				onEntryAdded();
			} else {
				toast.error(result.error || "Failed to add item");
			}
		} catch {
			toast.error("Failed to add item");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !showProductDropdown) {
			e.preventDefault();
			handleSave();
		} else if (e.key === "Escape") {
			e.preventDefault();
			resetForm();
		}
	};

	if (!isOpen) {
		return (
			<tr>
				<td
					colSpan={10}
					style={{
						padding: "8px 14px",
						borderTop: "1px dashed var(--color-dashboard-border-soft)",
					}}
				>
					<button
						type="button"
						onClick={() => setIsOpen(true)}
						className="inline-flex items-center gap-1.5 text-xs font-medium text-dashboard-accent-dark hover:text-dashboard-accent transition-colors"
						style={{
							background: "none",
							border: "none",
							cursor: "pointer",
							padding: "4px 0",
						}}
					>
						<Plus size={14} /> Add Item Inline
					</button>
				</td>
			</tr>
		);
	}

	return (
		<tr
			style={{
				background:
					"color-mix(in srgb, var(--color-dashboard-accent) 4%, var(--color-dashboard-paper))",
				borderBottom: "2px solid var(--color-dashboard-accent)",
			}}
			onKeyDown={handleKeyDown}
		>
			{/* Line # */}
			<td
				style={{
					fontFamily: "var(--font-mono)",
					fontSize: 11,
					color: "var(--color-dashboard-placeholder)",
					padding: "8px 14px",
					verticalAlign: "middle",
				}}
			>
				{nextIndex}
			</td>

			{/* Product Search Combobox */}
			<td
				style={{
					padding: "8px 10px",
					minWidth: 180,
					verticalAlign: "middle",
					position: "relative",
				}}
				ref={dropdownRef}
			>
				<div style={{ position: "relative" }}>
					<input
						ref={searchInputRef}
						type="text"
						value={searchQuery}
						onChange={(e) => {
							setSearchQuery(e.target.value);
							// Editing the text drops the previous pick: the line is
							// whatever is typed here.
							setSelectedProduct(null);
							setIsCustomItem(false);
							setShowProductDropdown(true);
							debouncedSearch(e.target.value);
						}}
						onFocus={() => setShowProductDropdown(true)}
						placeholder="Search product..."
						aria-label="Search product"
						style={{
							width: "100%",
							padding: "6px 26px 6px 10px",
							fontSize: 13,
							fontFamily: "var(--font-body)",
							background: "var(--color-dashboard-paper)",
							border: "1px solid var(--color-dashboard-accent)",
							borderRadius: 6,
							color: "var(--color-dashboard-text)",
							outline: "none",
						}}
					/>
					<div
						style={{
							position: "absolute",
							right: 8,
							top: "50%",
							transform: "translateY(-50%)",
							pointerEvents: "none",
						}}
					>
						{isSearching ? (
							<Loader2
								size={12}
								className="animate-spin text-dashboard-muted"
							/>
						) : (
							<Search size={12} className="text-dashboard-muted" />
						)}
					</div>
				</div>

				{isCustomItem && (
					<div
						style={{
							fontSize: 10,
							marginTop: 3,
							color: "var(--color-dashboard-muted)",
						}}
					>
						Custom item &mdash; not from the catalogue
					</div>
				)}

				{/* Dropdown Results */}
				{showProductDropdown && (
					<div
						style={{
							position: "absolute",
							top: "100%",
							left: 10,
							zIndex: 50,
							width: 280,
							maxHeight: 220,
							overflowY: "auto",
							background: "var(--color-dashboard-paper)",
							border: "1px solid var(--color-dashboard-border)",
							borderRadius: 8,
							boxShadow: "var(--dashboard-shadow-elevation-2)",
							padding: 4,
							marginTop: 4,
						}}
					>
						{searchQuery.trim() && (
							<button
								type="button"
								onClick={handleChooseCustom}
								style={{
									display: "block",
									width: "100%",
									textAlign: "left",
									padding: "8px 10px",
									fontSize: 12,
									fontWeight: 600,
									borderRadius: 5,
									border: "none",
									background: "transparent",
									cursor: "pointer",
									color: "var(--color-dashboard-accent-dark)",
								}}
								className="hover:bg-dashboard-surface"
							>
								+ Add &ldquo;{searchQuery.trim()}&rdquo; as a custom item
							</button>
						)}
						{products.length === 0 ? (
							<div
								style={{
									padding: "8px 12px",
									fontSize: 12,
									color: "var(--color-dashboard-muted)",
								}}
							>
								{isSearching ? "Searching catalogue..." : "No products found"}
							</div>
						) : (
							products.map((prod) => (
								<button
									key={prod.id}
									type="button"
									onClick={() => handleSelectProduct(prod)}
									style={{
										display: "block",
										width: "100%",
										textAlign: "left",
										padding: "8px 10px",
										fontSize: 12,
										borderRadius: 5,
										border: "none",
										background:
											selectedProduct?.id === prod.id
												? "var(--color-dashboard-surface)"
												: "transparent",
										cursor: "pointer",
										color: "var(--color-dashboard-text)",
									}}
									className="hover:bg-dashboard-surface"
								>
									<div style={{ fontWeight: 500 }}>{prod.name}</div>
									{prod.categoryName && (
										<div
											style={{
												fontSize: 10,
												color: "var(--color-dashboard-muted)",
											}}
										>
											{prod.categoryName}
										</div>
									)}
								</button>
							))
						)}
					</div>
				)}
			</td>

			{/* Description Input */}
			<td style={{ padding: "8px 10px", verticalAlign: "middle" }}>
				<input
					type="text"
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					placeholder="Specification / description"
					aria-label="Item description"
					style={{
						width: "100%",
						padding: "6px 10px",
						fontSize: 13,
						fontFamily: "var(--font-body)",
						background: "var(--color-dashboard-paper)",
						border: "1px solid var(--color-dashboard-border)",
						borderRadius: 6,
						color: "var(--color-dashboard-text)",
						outline: "none",
					}}
				/>
			</td>

			{/* Room Input */}
			<td style={{ padding: "8px 10px", width: 110, verticalAlign: "middle" }}>
				<input
					type="text"
					value={roomName}
					onChange={(e) => setRoomName(e.target.value)}
					placeholder="Room / space"
					aria-label="Room name"
					style={{
						width: "100%",
						padding: "6px 10px",
						fontSize: 12,
						fontFamily: "var(--font-body)",
						background: "var(--color-dashboard-paper)",
						border: "1px solid var(--color-dashboard-border)",
						borderRadius: 6,
						color: "var(--color-dashboard-text)",
						outline: "none",
					}}
				/>
			</td>

			{/* UoM */}
			<td
				style={{
					padding: "8px 10px",
					width: 75,
					verticalAlign: "middle",
					textAlign: "right",
				}}
			>
				<select
					value={uom}
					onChange={(e) => setUom(e.target.value as UoM)}
					aria-label="Unit of measure"
					style={{
						width: "100%",
						padding: "6px 4px",
						fontSize: 11,
						fontFamily: "var(--font-mono)",
						background: "var(--color-dashboard-paper)",
						border: "1px solid var(--color-dashboard-border)",
						borderRadius: 6,
						color: "var(--color-dashboard-text)",
						outline: "none",
					}}
				>
					<option value="sq_ft">sq ft</option>
					<option value="running_ft">rft</option>
					<option value="nos">nos</option>
					<option value="piece">piece</option>
					<option value="set">set</option>
					<option value="kg">kg</option>
					<option value="liter">litre</option>
					<option value="meter">meter</option>
					<option value="job">job</option>
				</select>
			</td>

			{/* Qty Input */}
			<td style={{ padding: "8px 10px", width: 85, verticalAlign: "middle" }}>
				<input
					type="number"
					step="any"
					min="0.01"
					value={quantity}
					onChange={(e) => setQuantity(e.target.value)}
					aria-label="Item quantity"
					style={{
						width: "100%",
						textAlign: "right",
						padding: "6px 8px",
						fontSize: 12,
						fontFamily: "var(--font-mono)",
						background: "var(--color-dashboard-paper)",
						border: "1px solid var(--color-dashboard-border)",
						borderRadius: 6,
						color: "var(--color-dashboard-text)",
						outline: "none",
					}}
				/>
			</td>

			{/* Unit Price (₹) Input */}
			<td style={{ padding: "8px 10px", width: 120, verticalAlign: "middle" }}>
				<div
					style={{
						position: "relative",
						display: "flex",
						alignItems: "center",
					}}
				>
					<span
						style={{
							position: "absolute",
							left: 8,
							fontSize: 11,
							color: "var(--color-dashboard-muted)",
							pointerEvents: "none",
						}}
					>
						₹
					</span>
					<input
						type="number"
						step="any"
						min="0"
						value={unitPrice}
						onChange={(e) => setUnitPrice(e.target.value)}
						placeholder="0"
						aria-label="Item unit price"
						style={{
							width: "100%",
							textAlign: "right",
							padding: "6px 8px 6px 20px",
							fontSize: 12,
							fontFamily: "var(--font-mono)",
							background: "var(--color-dashboard-paper)",
							border: "1px solid var(--color-dashboard-border)",
							borderRadius: 6,
							color: "var(--color-dashboard-text)",
							outline: "none",
						}}
					/>
				</div>
			</td>

			{/* Dynamic Live Line Total */}
			<td
				style={{
					fontFamily: "var(--font-display)",
					fontSize: 13,
					fontWeight: 600,
					color: "var(--color-dashboard-accent-dark)",
					padding: "8px 14px",
					textAlign: "right",
					verticalAlign: "middle",
				}}
			>
				{formatCurrency(liveTotalPaisa, currencyCode)}
			</td>

			{/* Approved Status */}
			<td
				style={{
					padding: "8px 14px",
					textAlign: "center",
					verticalAlign: "middle",
				}}
			>
				<span style={{ fontSize: 11, color: "var(--color-dashboard-muted)" }}>
					New
				</span>
			</td>

			{/* Actions: Save & Cancel */}
			<td
				style={{
					padding: "8px 14px",
					textAlign: "right",
					verticalAlign: "middle",
				}}
			>
				<div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
					<button
						type="button"
						onClick={handleSave}
						disabled={isSubmitting || !(selectedProduct || canSaveCustom)}
						title="Save item (Enter)"
						aria-label="Save new item"
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 28,
							height: 28,
							borderRadius: 5,
							background: "var(--color-dashboard-accent)",
							color: "var(--color-dashboard-on-fill)",
							border: "none",
							cursor:
								isSubmitting || !selectedProduct ? "not-allowed" : "pointer",
							opacity: isSubmitting || !selectedProduct ? 0.6 : 1,
						}}
					>
						{isSubmitting ? (
							<Loader2 size={13} className="animate-spin" />
						) : (
							<Check size={13} />
						)}
					</button>
					<button
						type="button"
						onClick={resetForm}
						disabled={isSubmitting}
						title="Cancel (Esc)"
						aria-label="Cancel new item"
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 28,
							height: 28,
							borderRadius: 5,
							background: "transparent",
							border: "1px solid var(--color-dashboard-border)",
							color: "var(--color-dashboard-muted)",
							cursor: "pointer",
						}}
					>
						<X size={13} />
					</button>
				</div>
			</td>
		</tr>
	);
}
